import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleIcon,
  CollapsibleTrigger,
} from '@wartek-id/collapsible-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'

import {
  Card,
  CardBody,
  CardHeader,
} from 'app/KinerjaPage/pages/PelaksanaanV2/components/Card'
import { CekHasilTindakLanjut } from 'app/KinerjaPage/pages/PelaksanaanV2/components/CekHasilTindakLanjut'
import { TujuanDialog } from 'app/KinerjaPage/pages/PelaksanaanV2/components/TujuanDialog'
import styles from './styles.module.css'

import type { MultiPeriodParams } from 'types/kinerja'
import type { IFollowUpDocumentForm } from 'types/kinerja-pelaksanaan'
import type { HeadingLevel } from 'types/typography'

export type FormTindakLanjutObservasiKelasProps = {
  headingLevel?: HeadingLevel
  isDisabled?: boolean
  isReadOnly?: boolean
  isKS?: boolean
  periodParams?: MultiPeriodParams
  ptkId?: string
  onClickFillingGuide?: () => void
  onClickObservationBackButton?: () => void
  onClickObservationButton?: () => void
  onClickTargetButton?: () => void
  onClickUnderstandTargetButton?: () => void
}
export function FormTindakLanjutObservasiKelas({
  headingLevel = 'h2',
  isDisabled = false,
  isReadOnly = false,
  periodParams,
  ptkId,
  onClickFillingGuide,
  onClickObservationBackButton,
  onClickObservationButton,
  onClickUnderstandTargetButton,
  isKS = false,
}: FormTindakLanjutObservasiKelasProps) {
  const formContext = useFormContext<IFollowUpDocumentForm>()
  const [isPanduanExpanded, setPanduanExpanded] = useState(true)

  const [isHasilPenilaianModalShown, setHasilPenilaianModalShown] =
    useState(false)
  const [isTujuanModalShown, setTujuanModalShown] = useState(false)

  const handlePanduanToggle = (isOpen: boolean) => {
    onClickFillingGuide?.()

    setPanduanExpanded(isOpen)
  }

  if (!formContext) {
    throw new Error(
      'FormTindakLanjutObservasiKelas must be used within `<FormProvider />`'
    )
  }

  const { register, getValues } = formContext

  return (
    <>
      <Card>
        <CardHeader className="flex space-x-4">
          <Text variant="heading-lg" className="flex-1" as={headingLevel}>
            Rencana Tindak Lanjut
          </Text>
        </CardHeader>

        <CardBody className="p-6">
          <div className="space-y-4 max-w-full lg:w-4/6">
            <div className="space-y-2">
              <Text variant="heading-sm">
                Bagaimana refleksi Anda tentang Praktik Kinerja Anda selama
                Observasi Praktik Kinerja?
              </Text>

              <fieldset className="space-y-1">
                <Text
                  as="label"
                  htmlFor="refleksi-guru"
                  color="subdued"
                  aria-hidden={isReadOnly}
                >
                  Refleksi Guru
                </Text>
                {isReadOnly ? (
                  <>
                    <Text className="overflow-wrap-anywhere" aria-hidden="true">
                      {getValues('selfReflection')}
                    </Text>
                    <span className="sr-only">
                      Refleksi Guru, {getValues('selfReflection')}
                    </span>
                  </>
                ) : (
                  <Textarea
                    {...register('selfReflection', {
                      required: true,
                      maxLength: 1000,
                    })}
                    isDisabled={isDisabled}
                    id="refleksi-guru"
                    maxLength={1000}
                    placeholder="Ketik di sini"
                    rows={3}
                  />
                )}
              </fieldset>
            </div>

            {!isKS && (
              <Collapsible
                defaultOpen
                open={isPanduanExpanded}
                variant="card"
                onToggle={handlePanduanToggle}
                className={styles.collapsible}
              >
                <CollapsibleTrigger type="button" className="!py-4">
                  <Text variant="heading-sm">Panduan Pengisian Refleksi</Text>
                  <CollapsibleIcon />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 space-y-5">
                    <div>
                      <Text variant="body-sm" color="subdued">
                        Tantangan bisa berupa salah satu kondisi berikut ini:
                      </Text>
                      <ul className="list-disc list-inside pl-2">
                        <Text as="li" variant="body-sm" color="subdued">
                          Kesulitan yang Anda hadapi
                        </Text>
                        <Text as="li" variant="body-sm" color="subdued">
                          Aspek praktik yang ingin Anda tingkatkan
                          efektivitasnya
                        </Text>
                        <Text as="li" variant="body-sm" color="subdued">
                          Perubahan praktik agar lebih berdampak pada
                          pembelajaran
                        </Text>
                      </ul>
                    </div>
                    <div>
                      <Text variant="body-sm" color="subdued">
                        Jelaskan aspek tantangan tersebut setidaknya:
                      </Text>
                      <ul className="list-disc list-inside pl-2">
                        <Text as="li" variant="body-sm" color="subdued">
                          Perilaku atau kejadian yang menggambarkan tantangan
                        </Text>
                        <Text as="li" variant="body-sm" color="subdued">
                          Faktor yang menyebabkan tantangan tersebut
                        </Text>
                        <Text as="li" variant="body-sm" color="subdued">
                          Dampak dari tantangan tersebut pada pembelajaran
                        </Text>
                      </ul>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            <button
              type="button"
              className="flex items-center space-x-1 text-blue-70 py-3"
              aria-haspopup={true}
              aria-expanded={isHasilPenilaianModalShown}
              onClick={() => {
                onClickObservationButton?.()
                setHasilPenilaianModalShown(true)
              }}
            >
              <Text as="span" variant="action" color="inherit">
                Cek Hasil Observasi Praktik Kinerja
              </Text>
              <Icon color="inherit">chevron_right</Icon>
            </button>
          </div>
        </CardBody>
      </Card>

      <Dialog
        closeOnOverlayClick={true}
        isOpen={isHasilPenilaianModalShown}
        size="lg"
        onClose={() => setHasilPenilaianModalShown(false)}
      >
        <DialogOverlay />
        <DialogContent>
          <CekHasilTindakLanjut
            ptkId={ptkId}
            periodParams={periodParams}
            nextButtonText="Kembali"
            onNext={() => {
              onClickObservationBackButton?.()
              setHasilPenilaianModalShown(false)
            }}
          />
        </DialogContent>
      </Dialog>

      <TujuanDialog
        title="Formulir Diskusi Tindak Lanjut"
        suggestion="Diskusi dengan atasan agar tujuan formulir ini tercapai."
        isOpen={isTujuanModalShown}
        onClose={() => {
          onClickUnderstandTargetButton?.()
          setTujuanModalShown(false)
        }}
        content={[
          {
            title: 'Refleksi Rating Observasi dari atasan',
            description:
              'Guru melakukan refleksi sebagai tanggapan terhadap catatan dan Rating Observasi yang diberikan oleh atasan.',
          },
          {
            title: 'Menyepakati tindak lanjut',
            description:
              'Guru dan atasan menyepakati tindak lanjut serta dukungan yang dibutuhkan untuk melakukan peningkatan Praktik Kinerja.',
          },
        ]}
      />
    </>
  )
}
